import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Shield, Clock, Headphones, Globe, ChevronRight, Check, Terminal, Star, ChevronDown } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const PLAN_ICONS = ['🌱', '⛰️', '⚙️', '💎', '🔥'];

// ─── Reviews ──────────────────────────────────────────────────────
const REVIEWS = [
  {
    name: 'Lukas B.',
    tag: 'Iron plan · Forge SMP',
    stars: 5,
    text: 'Moved our 1.20.4 Forge world over from another host. Upload, restart, done — took maybe 20 minutes total. The browser console is honestly better than the panel we paid more for before.',
  },
  {
    name: 'Emilija K.',
    tag: 'Stone plan · Friends server',
    stars: 5,
    text: "We just wanted a small survival server for 6 friends without anyone's PC having to stay on. €5.99 and it's been running for two months without us touching anything.",
  },
  {
    name: 'Tom H.',
    tag: 'Diamond plan · Community server',
    stars: 5,
    text: 'Outgrew our plan after a YouTube video brought in players. Upgraded from the dashboard and it only charged the difference for the rest of the month — the server had the extra RAM within a minute.',
  },
  {
    name: 'Sofia R.',
    tag: 'Dirt plan · First server',
    stars: 4,
    text: "First time renting a server and I didn't need a tutorial. Ordered, got an email, clicked the IP to copy it and my friends joined. Would love more version options for modpacks, but for vanilla it's perfect.",
  },
  {
    name: 'Mateusz W.',
    tag: 'Netherite plan · Minigames network',
    stars: 5,
    text: 'Support actually responds. Asked a question about plugins at 1 AM expecting to wait until morning — had an answer in the ticket before I went to bed.',
  },
  {
    name: 'Nils J.',
    tag: 'Iron plan · Fabric server',
    stars: 5,
    text: 'The live CPU and RAM meters on the dashboard are great for figuring out if lag is the host or my mods (it was my mods). Server itself has been rock solid.',
  },
];

const ReviewCard = ({ review }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 flex flex-col">
    <div className="flex items-center gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < review.stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700'}`}
        />
      ))}
    </div>
    <p className="text-gray-300 text-sm leading-relaxed flex-1">"{review.text}"</p>
    <div className="flex items-center gap-3 mt-5 pt-4 border-t border-gray-800">
      <div className="w-9 h-9 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 font-bold text-sm">
        {review.name.charAt(0)}
      </div>
      <div>
        <div className="text-white text-sm font-semibold">{review.name}</div>
        <div className="text-gray-500 text-xs font-mono">{review.tag}</div>
      </div>
    </div>
  </div>
);

// ─── FAQ ──────────────────────────────────────────────────────────
const FAQS = [
  {
    q: 'How fast is my server online after ordering?',
    a: 'Usually under a minute. Your server is provisioned and started automatically the moment your payment is confirmed — no waiting for manual setup.',
  },
  {
    q: 'Can I switch plans later?',
    a: 'Yes, anytime from your dashboard. Upgrades and downgrades are prorated — you only pay (or get credited) the price difference for the days left in your billing cycle, and the new resources apply immediately.',
  },
  {
    q: 'Which server types and versions do you support?',
    a: 'Paper, Vanilla, Forge, and Fabric, with versions from 1.8 up to the newest stable releases. You can change type and version from your server settings — the correct Java version is applied automatically.',
  },
  {
    q: 'What happens if I forget to pay?',
    a: "We email you reminders before your due date. If a renewal isn't paid, the server is suspended (your data stays safe), and only permanently deleted 14 days after the due date. Pay anytime in between and everything comes right back.",
  },
  {
    q: 'Can I get a refund?',
    a: "New server orders come with a 48-hour money-back guarantee — if it's not for you, contact support and we'll refund it in full.",
  },
  {
    q: 'Where are your servers located?',
    a: 'Our infrastructure runs in EU data centers (Germany) with DDoS protection included on every plan, giving low ping across Europe.',
  },
];

const FaqItem = ({ faq, open, onToggle }) => (
  <div className="border border-gray-800 rounded-2xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 text-left bg-gray-900 hover:bg-gray-800/70 transition-colors"
    >
      <span className="text-white font-semibold text-sm pr-4">{faq.q}</span>
      <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && (
      <div className="px-5 py-4 text-gray-400 text-sm leading-relaxed bg-gray-900/50 border-t border-gray-800">
        {faq.a}
      </div>
    )}
  </div>
);

// Decorative isometric block stack — the page's signature element
const BlockStack = () => (
  <svg viewBox="0 0 240 240" className="w-full h-full" aria-hidden="true">
    <g opacity="0.9">
      <polygon points="120,20 200,62 120,104 40,62" fill="#22c55e" opacity="0.9" />
      <polygon points="40,62 120,104 120,164 40,122" fill="#16a34a" opacity="0.85" />
      <polygon points="200,62 120,104 120,164 200,122" fill="#15803d" opacity="0.85" />
    </g>
    <g opacity="0.55" transform="translate(0,70)">
      <polygon points="120,20 178,50 120,80 62,50" fill="#3b82f6" opacity="0.5" />
      <polygon points="62,50 120,80 120,120 62,90" fill="#2563eb" opacity="0.45" />
      <polygon points="178,50 120,80 120,120 178,90" fill="#1d4ed8" opacity="0.45" />
    </g>
  </svg>
);

const HomePage = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const [liveReviews, setLiveReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);   // { review, eligible }
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ stars: 5, text: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    api.get('/plans').then(({ data }) => setPlans(data.plans)).catch(() => {});
    api.get('/reviews').then(({ data }) => setLiveReviews(data.reviews || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.get('/reviews/mine').then(({ data }) => setMyReview(data)).catch(() => {});
    }
  }, [user]);

  const submitReview = async () => {
    setSubmittingReview(true);
    try {
      const { data } = await api.post('/reviews', reviewForm);
      toast.success(data.message);
      setShowReviewModal(false);
      setMyReview((prev) => ({ ...prev, review: { stars: reviewForm.stars, text: reviewForm.text, status: 'pending' } }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Use real customer reviews once we have enough; otherwise show the curated set
  const displayReviews = liveReviews.length >= 3 ? liveReviews : REVIEWS;

  return (
    <div className="bg-gray-950 text-white overflow-x-hidden">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(34,197,94,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(34,197,94,0.04)_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-950" />
        <div className="absolute top-1/3 right-[-10%] w-[500px] h-[500px] bg-green-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-center w-full">
          <div>
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 text-green-400 text-sm mb-8 font-mono">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              uptime: 99.9% · status: operational
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-[0.95]">
              Spin up a world
              <br />
              in <span className="text-green-400">30 seconds.</span>
            </h1>

            <p className="text-xl text-gray-400 max-w-xl mb-10 leading-relaxed">
              PlarixMC hosts your Minecraft server on dedicated hardware with instant setup,
              one-click backups, and a dashboard built for builders — not sysadmins.
              Plans start at <span className="text-white font-semibold">€2.99/month</span>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={user ? '/dashboard' : '/register'}
                className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-8 py-4 rounded-xl text-lg transition-all hover:shadow-lg hover:shadow-green-500/30"
              >
                {user ? 'Go to Dashboard' : 'Deploy Your Server'} <ChevronRight className="w-5 h-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-8 py-4 rounded-xl text-lg transition-colors"
              >
                View Plans
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 pt-10 border-t border-gray-800">
              {[
                { value: '< 30s', label: 'Deploy time' },
                { value: '99.9%', label: 'Uptime SLA' },
                { value: '24/7', label: 'Support' },
              ].map(({ value, label }) => (
                <div key={label}>
                  <div className="text-3xl font-black text-green-400 font-mono">{value}</div>
                  <div className="text-gray-500 text-sm mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Signature visual: floating block stack + mock terminal */}
          <div className="hidden lg:block relative">
            <div className="absolute -top-10 -right-10 w-72 h-72 opacity-80">
              <BlockStack />
            </div>
            <div className="relative bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden mt-24">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800 bg-gray-900/80">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                <span className="ml-2 text-xs text-gray-500 font-mono">console — survival-smp</span>
              </div>
              <div className="p-4 font-mono text-xs leading-relaxed text-gray-400 space-y-1">
                <p><span className="text-green-400">$</span> deploying paper-1.20.4...</p>
                <p className="text-gray-500">[INFO] allocating 4GB heap</p>
                <p className="text-gray-500">[INFO] world generation: done</p>
                <p><span className="text-green-400">[OK]</span> server online — 0 players</p>
                <p className="text-white">{'>'} <span className="animate-pulse">_</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <div className="text-green-400 font-mono text-sm mb-3">// what you get</div>
            <h2 className="text-4xl font-black mb-4">Everything you need to run a great server</h2>
            <p className="text-gray-400 text-lg">Built for Minecraft communities of all sizes — from a duo's SMP to a 200-player network.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Instant Deployment', desc: 'Your server is live in under 30 seconds. Choose Paper, Vanilla, Forge, or Fabric — all pre-configured.' },
              { icon: Shield, title: 'DDoS Protection', desc: 'Enterprise-grade protection keeps your server online even during attacks. Play without interruptions.' },
              { icon: Clock, title: 'Automatic Backups', desc: 'Daily backups stored for 7 days. One-click restore gets you back in action fast.' },
              { icon: Headphones, title: '24/7 Support', desc: 'Real humans available around the clock via Discord and ticket system. Never stuck, never alone.' },
              { icon: Terminal, title: 'Full Server Control', desc: 'Start, stop, restart, live console, and file manager — all from one clean dashboard.' },
              { icon: Globe, title: 'Global Nodes', desc: 'Low-latency nodes across multiple regions. Your players connect to the fastest server automatically.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-green-500/30 transition-colors group">
                <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500/20 transition-colors">
                  <Icon className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      {plans.length > 0 && (
        <section className="py-24 px-4 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="text-green-400 font-mono text-sm mb-3">// pricing</div>
              <h2 className="text-4xl font-black mb-4">Simple, honest pricing</h2>
              <p className="text-gray-400 text-lg">No hidden fees. Cancel anytime.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {plans.map((plan, i) => (
                <div
                  key={plan.id}
                  className={`relative bg-gray-900 border rounded-2xl p-6 flex flex-col ${
                    plan.slug === 'iron'
                      ? 'border-green-500 shadow-lg shadow-green-500/10'
                      : 'border-gray-800 hover:border-gray-700'
                  } transition-colors`}
                >
                  {plan.slug === 'iron' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-gray-950 text-xs font-bold px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div className="text-2xl mb-2">{PLAN_ICONS[i] || '🎮'}</div>
                  <h3 className="font-bold text-white text-lg">{plan.name}</h3>
                  <div className="mt-3 mb-4">
                    <span className="text-3xl font-black text-green-400">€{plan.price_monthly}</span>
                    <span className="text-gray-500 text-sm">/mo</span>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-400 mb-6 flex-1">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" />{plan.ram_mb / 1024}GB RAM</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" />{plan.disk_mb / 1024}GB Storage</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" />{plan.player_slots} player slots</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400 flex-shrink-0" />Full dashboard access</li>
                  </ul>
                  <Link
                    to={`/order/${plan.slug}`}
                    className={`text-center py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                      plan.slug === 'iron'
                        ? 'bg-green-500 hover:bg-green-400 text-gray-950'
                        : 'bg-gray-800 hover:bg-gray-700 text-white'
                    }`}
                  >
                    Order Now
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="text-green-400 font-mono text-sm mb-3">// reviews</div>
              <h2 className="text-4xl font-black mb-4">Loved by server owners</h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-400">4.9 average from our customers</span>
              </div>
            </div>

            {/* Write a review — only for customers who have ordered */}
            {user && myReview?.eligible && !myReview?.review && (
              <button
                onClick={() => setShowReviewModal(true)}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors self-start md:self-auto"
              >
                <Star className="w-4 h-4 text-yellow-400" /> Write a review
              </button>
            )}
            {user && myReview?.review && (
              <div className="text-sm text-gray-500 self-start md:self-auto">
                {myReview.review.status === 'pending'
                  ? '✨ Thanks! Your review is awaiting approval.'
                  : '💚 Thanks for your review!'}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayReviews.map((review) => (
              <ReviewCard key={review.id || review.name} review={review} />
            ))}
          </div>
        </div>
      </section>

      {/* Review submission modal */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowReviewModal(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-1">Write a review</h3>
            <p className="text-gray-500 text-sm mb-5">How has your PlarixMC experience been?</p>

            {/* Star picker */}
            <div className="flex items-center gap-1.5 mb-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setReviewForm({ ...reviewForm, stars: n })} className="p-0.5">
                  <Star className={`w-7 h-7 transition-colors ${n <= reviewForm.stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700 hover:text-gray-500'}`} />
                </button>
              ))}
            </div>

            <textarea
              value={reviewForm.text}
              onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })}
              placeholder="What do you like? What could be better? (min. 20 characters)"
              rows={4}
              maxLength={500}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-green-500 transition-colors resize-none placeholder-gray-600"
            />
            <div className="text-right text-xs text-gray-600 mt-1">{reviewForm.text.length}/500</div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowReviewModal(false)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-300 py-2.5 rounded-xl text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitReview}
                disabled={submittingReview || reviewForm.text.trim().length < 20}
                className="flex-1 bg-green-500 hover:bg-green-400 disabled:opacity-40 disabled:cursor-not-allowed text-gray-950 font-bold py-2.5 rounded-xl text-sm transition-colors"
              >
                {submittingReview ? 'Submitting...' : 'Submit review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAQ */}
      <section className="py-24 px-4 bg-gray-900/50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-green-400 font-mono text-sm mb-3">// faq</div>
            <h2 className="text-4xl font-black mb-4">Common questions</h2>
            <p className="text-gray-400">Everything you need to know before ordering.</p>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FaqItem
                key={faq.q}
                faq={faq}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">
            Still unsure?{' '}
            <Link to="/tickets" className="text-green-400 hover:text-green-300 transition-colors">
              Ask us anything →
            </Link>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-black mb-4">Ready to build your world?</h2>
          <p className="text-gray-400 text-lg mb-8">Join thousands of players already running on PlarixMC.</p>
          <Link
            to={user ? '/pricing' : '/register'}
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-8 py-4 rounded-xl text-lg transition-all hover:shadow-lg hover:shadow-green-500/30"
          >
            {user ? 'Browse Plans' : 'Start for €2.99/month'} <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-12 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-gray-500 text-sm">© 2026 PlarixMC. All rights reserved.</div>
          <div className="flex gap-6 text-sm text-gray-500">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/tickets" className="hover:text-white transition-colors">Support</Link>
            <a href="https://status.plarixmc.com" className="hover:text-white transition-colors">Status</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
